import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';

import { TabRewardsPng } from '../../../assets/event';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import { EVENT_FONT, eventColors, eventGlow, TAB_ACTIVE_FILL } from './eventTheme';

// Figma "Tabs / rewards selected" (3324:18935): 360x44, two 180-wide halves.
export const TABS_DESIGN_WIDTH = 360;
export const TABS_DESIGN_HEIGHT = 44;

export type EventTab = 'rewards' | 'info';

type Props = {
	value: EventTab;
	onChange: (tab: EventTab) => void;
	width: number;
};

const InfoIcon = ({ size, color }: { size: number; color: string }) => (
	<Svg width={size} height={size} viewBox='0 0 16 16'>
		<Circle cx='8' cy='8' r='7' stroke={color} strokeWidth='1.2' fill='none' />
		<Circle cx='8' cy='4.6' r='0.95' fill={color} />
		<Path d='M8 7.2v4.6' stroke={color} strokeWidth='1.4' strokeLinecap='round' />
	</Svg>
);

export const EventTabs = ({ value, onChange, width }: Props) => {
	const { t } = useTranslation();
	const scale = width / TABS_DESIGN_WIDTH;
	const styles = useMemo(() => createStyles(scale), [scale]);
	const rewardsFillId = useSvgId('eventTabRewards');
	const infoFillId = useSvgId('eventTabInfo');

	const activePlate = (fillId: string) => (
		<View pointerEvents='none' style={[styles.plate, { borderRadius: 10 * scale }]}>
			<Svg width='100%' height='100%'>
				<Defs>
					<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
						{TAB_ACTIVE_FILL.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
			</Svg>
		</View>
	);

	return (
		<View style={styles.row}>
			<Pressable
				accessibilityRole='tab'
				accessibilityState={{ selected: value === 'rewards' }}
				onPress={() => onChange('rewards')}
				style={[styles.tab, value === 'rewards' ? styles.tabActive : styles.tabInactive]}
			>
				{value === 'rewards' ? activePlate(rewardsFillId) : null}
				<Image source={TabRewardsPng} style={styles.giftIcon} resizeMode='contain' />
				<Text numberOfLines={1} style={styles.label}>
					{t('T_Rewards', { defaultValue: 'REWARDS' })}
				</Text>
			</Pressable>

			<Pressable
				accessibilityRole='tab'
				accessibilityState={{ selected: value === 'info' }}
				onPress={() => onChange('info')}
				style={[styles.tab, value === 'info' ? styles.tabActive : styles.tabInactive]}
			>
				{value === 'info' ? activePlate(infoFillId) : null}
				<InfoIcon size={16 * scale} color={eventColors.accent} />
				<Text numberOfLines={1} style={styles.label}>
					{t('T_SITE_EVENT_TAB_INFO', { defaultValue: 'INFO' })}
				</Text>
			</Pressable>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		row: {
			width: TABS_DESIGN_WIDTH * scale,
			height: TABS_DESIGN_HEIGHT * scale,
			flexDirection: 'row',
			borderRadius: 9 * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: eventColors.panelBorder,
			backgroundColor: eventColors.surface,
			// No overflow clip: it cut the active tab's bloom off at the row's edge and, on iOS, hid
			// the row's own glow. The tabs' 10 corners already sit inside the row's 9.
			...eventGlow.panel,
		},
		plate: {
			...StyleSheet.absoluteFillObject,
			overflow: 'hidden',
		},
		tab: {
			flex: 1,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: 5 * scale,
			borderRadius: 10 * scale,
		},
		tabActive: {
			borderWidth: Math.max(1, 1 * scale),
			borderColor: eventColors.panelBorderBright,
			...eventGlow.bloom,
		},
		tabInactive: {
			backgroundColor: 'transparent',
		},
		giftIcon: {
			width: 23 * scale,
			height: 23 * scale,
		},
		// Figma "Label/Overline": Sofia Sans Black 14, letter-spacing 1.12.
		label: {
			color: eventColors.white,
			fontFamily: EVENT_FONT,
			fontSize: 14 * scale,
			fontWeight: '900',
			letterSpacing: 1.12 * scale,
		},
	});

export default EventTabs;
